import type {
  CheckoutGeoPricingDiscount,
  CheckoutGeoPricingProductContext,
  CheckoutGeoPricingTestData,
} from "./types";

type GetCheckoutGeoPricingDiscountParams = {
  data?: CheckoutGeoPricingTestData;
  country: string;
  product?: CheckoutGeoPricingProductContext;
};

export const getCheckoutGeoPricingDiscountByCountry = ({
  data,
  country,
  product,
}: GetCheckoutGeoPricingDiscountParams): CheckoutGeoPricingDiscount | null => {
  const countryDiscount = data?.geoBasedDiscounts?.find(
    (baseDiscount) => baseDiscount.countryCode === country,
  );

  if (!countryDiscount) {
    return null;
  }

  const partnerGroups = data?.excludedPartnerGroups?.length
    ? data.excludedPartnerGroups
    : undefined;

  let partnerDiscount:
    | { discountCode: string; discountPercentage: number }
    | undefined;

  if (partnerGroups) {
    const matchingGroups = partnerGroups.filter((group) =>
      group.partnerKeys?.some((key) =>
        product?.partners?.some(
          (p) => p.key.toLowerCase() === key.toLowerCase(),
        ),
      ),
    );
    const mostRestrictive = matchingGroups.reduce<
      (typeof matchingGroups)[number] | undefined
    >(
      (prev, curr) =>
        !prev || curr.discountPercentage < prev.discountPercentage
          ? curr
          : prev,
      undefined,
    );
    partnerDiscount = mostRestrictive
      ? {
          discountCode: mostRestrictive.discountCode,
          discountPercentage: mostRestrictive.discountPercentage,
        }
      : undefined;
  } else if (data?.excludedPartners) {
    const excludedKeys = data.excludedPartners.partnerKeys?.map((k) =>
      k.toLowerCase(),
    );
    const hasPartnerDiscount = product?.partners?.some((partner) =>
      excludedKeys?.includes(partner.key.toLowerCase()),
    );
    partnerDiscount = hasPartnerDiscount
      ? {
          discountCode: data.excludedPartners.discountCode,
          discountPercentage: data.excludedPartners.discountPercentage,
        }
      : undefined;
  }

  const hasProductTypeDiscount =
    data?.excludedProductTypes?.productTypes.some(
      (type) => type.toLowerCase() === product?.productType?.toLowerCase(),
    ) ?? false;

  const productTypeDiscount = hasProductTypeDiscount
    ? {
        discountCode: data?.excludedProductTypes?.discountCode,
        discountPercentage: data?.excludedProductTypes?.discountPercentage,
      }
    : undefined;

  return {
    discountCode:
      partnerDiscount?.discountCode ??
      productTypeDiscount?.discountCode ??
      countryDiscount?.discountCode,
    discountPercentage:
      partnerDiscount?.discountPercentage ??
      productTypeDiscount?.discountPercentage ??
      countryDiscount?.discountPercentage,
    countryCode: countryDiscount?.countryCode,
  };
};
